import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, postForm } from '../api';
import { CATALOG_KEY } from './ItemForm';

type PreviewRow = { sheet: string; row: number; name: string; status: 'new' | 'changed' | 'error' | 'conflict'; changes?: string[]; message?: string };
export type ImportPreview = {
  counts: { new: number; changed: number; unchanged: number; errors: number; conflicts: number };
  rows: PreviewRow[];
  truncated: boolean;
  newCategories: string[];
  newLocations: string[];
  missingItems: number;
  missingUnits: number;
  fileErrors: string[];
  canApply: boolean;
  digest: string;
};

/** Same limit as the server's (modules/catalog routes-excel MAX_BYTES). */
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const isPreview = (v: unknown): v is ImportPreview => typeof v === 'object' && v !== null && 'counts' in v && 'digest' in v;
const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Nie udało się wczytać pliku.');

function rowText(r: PreviewRow): string {
  const what = r.status === 'new' ? 'nowy wiersz' : r.status === 'changed' ? `zmiana: ${(r.changes ?? []).join(', ')}` : (r.message ?? '');
  return `${r.sheet}, wiersz ${r.row}: ${r.name} — ${what}`;
}

/** What the import would do: counts, every row that is not unchanged, new categories and locations, what stays. */
function PreviewView({ preview, busy, onApply, onCancel }: { preview: ImportPreview; busy: boolean; onApply: () => void; onCancel: () => void }) {
  const { counts } = preview;
  const nothing = counts.new + counts.changed === 0;
  return (
    <section className="excel-preview" aria-label="Podgląd wczytania">
      <p>
        <strong>
          Dodam {counts.new}, zmienię {counts.changed}, bez zmian {counts.unchanged}
        </strong>
      </p>
      {(counts.errors > 0 || counts.conflicts > 0) && (
        <p className="alert">
          Błędy: {counts.errors}, konflikty: {counts.conflicts} – popraw plik i wczytaj go ponownie. Do tego czasu nic nie zostanie zmienione.
        </p>
      )}
      {preview.fileErrors.length > 0 && (
        <ul className="alert excel-file-errors">
          {preview.fileErrors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      {preview.rows.length > 0 && (
        <ul className="excel-rows">
          {preview.rows.map((r) => (
            <li key={`${r.sheet}:${r.row}`} className={`excel-row excel-row-${r.status}`}>
              {rowText(r)}
            </li>
          ))}
        </ul>
      )}
      {preview.truncated && <p className="muted">Lista jest skrócona – liczby u góry obejmują cały plik.</p>}
      {(preview.newCategories.length > 0 || preview.newLocations.length > 0) && (
        <ul className="excel-new">
          {preview.newCategories.map((c) => (
            <li key={`c:${c}`}>nowa kategoria: {c}</li>
          ))}
          {preview.newLocations.map((l) => (
            <li key={`l:${l}`}>nowa lokalizacja: {l}</li>
          ))}
        </ul>
      )}
      {preview.missingItems > 0 && <p className="muted">{preview.missingItems} pozycji z programu nie ma w pliku – zostaną bez zmian.</p>}
      {preview.missingUnits > 0 && <p className="muted">{preview.missingUnits} egzemplarzy z programu nie ma w pliku – zostaną bez zmian.</p>}
      {nothing && preview.canApply && <p className="muted">Plik niczego nie zmienia.</p>}
      <div className="actions">
        <button type="button" className="btn primary small" disabled={!preview.canApply || nothing || busy} onClick={onApply}>
          Zatwierdź
        </button>
        <button type="button" className="btn small" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </section>
  );
}

/**
 * „Eksportuj do Excela” and „Wczytaj z Excela” in the catalog (owner only; the server refuses everyone else): the
 * download is a plain link; the import shows the preview first and „Zatwierdź” sends the same file with its digest.
 */
export function CatalogExcel() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const form = (f: File, digest?: string) => {
    const body = new FormData();
    if (digest !== undefined) body.append('expected', digest);
    body.append('file', f);
    return body;
  };
  const check = useMutation({
    mutationFn: (f: File) => postForm<ImportPreview>('/api/catalog/excel/preview', form(f), MAX_FILE_BYTES),
    onMutate: () => {
      setError('');
      setPreview(null);
    },
    onSuccess: setPreview,
    onError: (e) => setError(errorText(e)),
  });
  const apply = useMutation({
    mutationFn: (p: { file: File; digest: string }) => postForm<ImportPreview>('/api/catalog/excel/apply', form(p.file, p.digest), MAX_FILE_BYTES),
    onSuccess: async (p) => {
      setDone(`Wczytano katalog: dodano ${p.counts.new}, zmieniono ${p.counts.changed}.`);
      close();
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
    },
    onError: (e) => {
      setError(errorText(e));
      // the server sends the new plan with its refusal: show it instead of the old one
      const fresh = e instanceof ApiError ? (e.status === 409 ? e.current : e.details) : null;
      if (isPreview(fresh)) setPreview(fresh);
    },
  });
  function close() {
    setOpen(false);
    setFile(null);
    setPreview(null);
    setError('');
  }
  return (
    <div className="catalog-excel" data-feature-id="catalog.excel">
      <div className="catalog-excel-links">
        <a href="/api/catalog/excel" download>
          Eksportuj do Excela
        </a>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            setDone('');
            if (open) close();
            else setOpen(true);
          }}
        >
          Wczytaj z Excela
        </button>
      </div>
      {done && (
        <p className="notice" role="status">
          {done}
        </p>
      )}
      {open && (
        <div className="catalog-excel-panel">
          <label className="field">
            <span>Plik katalogu (.xlsx)</span>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f) check.mutate(f);
              }}
            />
          </label>
          <p className="hint">Tylko plik pobrany przez „Eksportuj do Excela”. Najpierw zobaczysz podgląd – nic się nie zmieni, dopóki nie klikniesz „Zatwierdź”.</p>
          {check.isPending && <p className="muted">Sprawdzam plik…</p>}
          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          {preview && file && <PreviewView preview={preview} busy={apply.isPending} onApply={() => apply.mutate({ file, digest: preview.digest })} onCancel={close} />}
        </div>
      )}
    </div>
  );
}
